import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import moment from "moment";
import { Calendar } from ".";

interface ICalendarFilterProps {
    value?: DateRange;
    onApply: (range: DateRange) => void;
    minDate?: Date;
    maxDate?: Date;
    disabledPresets?: string[];
    onSelectDisabledPreset?: (label: string) => void;
}

const periods = [
    {
        label: "Hari ini",
        getRange: () => {
            const today = moment().startOf("day").toDate();
            return { from: today, to: today };
        },
    },
    {
        label: "1 minggu terakhir",
        getRange: () => ({
            from: moment().subtract(1, "week").startOf("day").toDate(),
            to: moment().startOf("day").toDate(),
        }),
    },
    {
        label: "1 bulan terakhir",
        getRange: () => ({
            from: moment().subtract(1, "month").startOf("day").toDate(),
            to: moment().startOf("day").toDate(),
        }),
    },
    {
        label: "3 bulan terakhir",
        getRange: () => ({
            from: moment().subtract(3, "month").startOf("day").toDate(),
            to: moment().startOf("day").toDate(),
        }),
    },
    {
        label: "1 tahun terakhir",
        getRange: () => ({
            from: moment().subtract(1, "year").startOf("day").toDate(),
            to: moment().startOf("day").toDate(),
        }),
    },
];

const CalendarFilter = ({
    value,
    onApply,
    minDate,
    maxDate,
    disabledPresets = [],
    onSelectDisabledPreset,
}: ICalendarFilterProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [draft, setDraft] = useState<DateRange | undefined>(value);
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (open) setDraft(value);
    }, [open, value]);

    const handleSelectPeriod = (label: string, getRange: () => DateRange) => {
        if (disabledPresets.includes(label)) {
            onSelectDisabledPreset?.(label);
            return;
        }
        setDraft(getRange());
    };

    const formatRangeLabel = () => {
        if (!value?.from) return "Pilih Tanggal";
        const from = moment(value.from).format("DD/MM/YYYY");
        const to = moment(value.to ?? value.from).format("DD/MM/YYYY");
        return `${from} - ${to}`;
    };

    const activePeriod = useMemo(() => {
        return periods.find(({ getRange }) => {
            const preset = getRange();
            return (
                moment(preset.from).isSame(draft?.from, "day") &&
                moment(preset.to).isSame(draft?.to ?? draft?.from, "day")
            );
        })?.label;
    }, [draft]);

    const disabledMatcher = useMemo(() => {
        if (minDate && maxDate) return [{ before: minDate }, { after: maxDate }];
        if (minDate) return { before: minDate };
        if (maxDate) return { after: maxDate };
        return { after: new Date() };
    }, [minDate, maxDate]);

    return (
        <Box width="100%">
            <Button
                variant="outlined"
                fullWidth
                aria-describedby="date-range-picker"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={
                    <CalendarTodayRoundedIcon htmlColor="rgba(0, 0, 0, 0.6)" />
                }
                sx={{
                    justifyContent: "space-between",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    textTransform: "none",
                    width: "100%",
                    height: "40px",
                    color: "text.primary",
                    paddingRight: 1,
                    backgroundColor: "#fff",
                }}
            >
                <Typography color="text.primary">
                    {formatRangeLabel()}
                </Typography>
                <ArrowDropDownIcon htmlColor="rgba(0, 0, 0, 0.6)" />
            </Button>
            <Popover
                id="date-range-picker"
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: {
                        p: 2,
                        borderRadius: 2,
                        width: isMobile ? "100%" : 620,
                        maxWidth: "90vw",
                    },
                }}
            >
                <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
                    <Box
                        flex={1}
                        sx={{ minWidth: 180, mb: isMobile ? 2 : 0 }}
                    >
                        <List dense>
                            {periods.map((period) => (
                                <ListItem key={period.label} disablePadding>
                                    <ListItemButton
                                        selected={period.label === activePeriod}
                                        onClick={() =>
                                            handleSelectPeriod(
                                                period.label,
                                                period.getRange,
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        disabledPresets.includes(
                                                            period.label,
                                                        )
                                                            ? "text.disabled"
                                                            : undefined
                                                    }
                                                >
                                                    {period.label}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                    {!isMobile && (
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 2 }}
                        />
                    )}
                    {isMobile && (
                        <Divider orientation="horizontal" sx={{ my: 2 }} />
                    )}
                    <Box>
                        <Calendar
                            autoFocus
                            mode="range"
                            selected={draft}
                            onSelect={setDraft}
                            pagedNavigation
                            disabled={disabledMatcher}
                        />
                    </Box>
                </Box>
                <Stack direction="row" justifyContent="flex-end" gap={2}>
                    <Button
                        variant="outlined"
                        onClick={() => setAnchorEl(null)}
                    >
                        Batal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (!draft?.from) return;
                            const end = draft.to ?? draft.from;
                            onApply({ from: draft.from, to: end });
                            setAnchorEl(null);
                        }}
                    >
                        Simpan
                    </Button>
                </Stack>
            </Popover>
        </Box>
    );
};

export default CalendarFilter;
